/** @jsxImportSource @emotion/react */
import { RefObject, useEffect } from 'react';
import * as S from './MultiSelectBase.styles';

export type SelectAllToolbarProps = {
  /** Checkbox ref (for indeterminate state) */
selectAllRef: RefObject<HTMLInputElement | null>;

  /** Whether all items in scope are selected */
  allSelected: boolean;

  /** Select-all checkbox indeterminate state. Pass it where selectedCount is a wider total than
   *  the toggle scope; otherwise derived from selectedCount. */
  indeterminate?: boolean;

  /** Number of selected items (in current scope) */
  selectedCount: number;

  /** Number of items currently shown (filtered list length) */
  shownCount: number;

  /** Whether filtering is active */
  isFiltered: boolean;

  /** Toggle select‑all */
  onToggle: (checked: boolean) => void;

  /** Clear all selection */
  onClear: () => void;

  showClear?: boolean;
  clearLabel?: string;

  /**
   * Overrides the "Select All" wording. Pass one that names the scope wherever
   * the toggle reaches beyond what's on screen (e.g. "Select all 812 results"
   * for a paged table) — plain "Select All" reads as "everything I can see".
   */
  selectAllLabel?: string;

  /** Hover text for the select-all control. Use it to say what the label can't. */
  selectAllTitle?: string;

  /** Hover text for the clear action. */
  clearTitle?: string;
};

export function SelectAllToolbar({
  selectAllRef,
  allSelected,
  indeterminate,
  selectedCount,
  shownCount,
  isFiltered,
  onToggle,
  onClear,
  showClear = true,
  clearLabel = 'Clear',
  selectAllLabel = 'Select All',
  selectAllTitle,
  clearTitle,
}: SelectAllToolbarProps) {

  useEffect(() => {
    if (!selectAllRef.current) return;

    selectAllRef.current.indeterminate =
      indeterminate ?? (selectedCount > 0 && !allSelected);
  }, [selectAllRef, indeterminate, selectedCount, allSelected]);

  return (
    <S.Toolbar>
      <label title={selectAllTitle}>
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allSelected && shownCount > 0}
          onChange={e => onToggle(e.target.checked)}
        />
        <span>
          {selectAllLabel}
          {isFiltered && ' (filtered)'}
        </span>
      </label>

      <span className="count">
        {selectedCount} selected
        {isFiltered && ` · ${shownCount} shown`}
      </span>

      {showClear && (
        <button
          type="button"
          className="action-btn"
          title={clearTitle}
          onClick={onClear}
          disabled={selectedCount === 0}
        >
          {clearLabel}
        </button>
      )}
    </S.Toolbar>
  );
}
